// external
import type { FC } from 'react';
import { useCallback } from 'react';

// interstate components
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';

// libraries
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { formatNumberAndCents } from '@makemydeal/dr-common-utils';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';

// utils
import { usePaymentCall } from '../utils/usePaymentCall';

// constants
import { RESIDUAL_FIELD_LABELS } from './constants';

export interface ResidualBasedOnFieldProps {
    disabled?: boolean;
    error?: string;
    'data-testid'?: string;
}

export const ResidualBasedOnField: FC<ResidualBasedOnFieldProps> = ({ disabled = false, error, 'data-testid': testId }) => {
    const {
        value: residualBasedOn,
        handleChange: handleResidualBasedOnChange,
        handleBlur,
        handleKeyDown
    } = usePaymentCall(
        offerSelectors.getRetailPriceOverrideWithFallback,
        offerReduxActionCreators.updateVehicleRetailPriceOverride,
        { formatValueToString: formatNumberAndCents }
    );

    const handleChange = useCallback(
        (e: InterstateOnChangeEvent<string>) => {
            handleResidualBasedOnChange(e);
        },
        [handleResidualBasedOnChange]
    );

    return (
        <NumericInput
            label={RESIDUAL_FIELD_LABELS.RESIDUAL_BASED_ON}
            name="residual-based-on"
            onChange={handleChange}
            placeholder="0.00"
            value={residualBasedOn}
            hasError={!!error}
            errorMessage={error}
            onBlur={handleBlur}
            data-testid={testId || 'residual-based-on-input'}
            allowDecimal={true}
            decimalMaxLength={2}
            decimalMinLength={2}
            disabled={disabled}
            inputPrefix="$"
            autoInsertCommas={true}
            onKeyDown={handleKeyDown}
            textAlign="left"
            selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
            strictSelectOnFocus
            aria-invalid={!!error}
            aria-describedby={error ? 'residual-based-on-error' : undefined}
        />
    );
};

export default ResidualBasedOnField;
